/**
 * Shared colour math used by Design-category widgets.
 * Pure functions, no DOM. All hex inputs accept #rgb or #rrggbb (case-insensitive).
 */

export type RGB = { r: number; g: number; b: number };
export type HSL = { h: number; s: number; l: number };

export function hexToRgb(hex: string): RGB | null {
  const m = hex.trim().replace(/^#/, "");
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(m)) return null;
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const sn = s / 100, ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hh = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = ln - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

export function hexToHsl(hex: string): HSL | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHsl(rgb) : null;
}

export function hslToHex(hsl: HSL): string {
  return rgbToHex(hslToRgb(hsl));
}

/**
 * Generate a Tailwind-style 11-step shade scale (50,100..900,950) from a base hex.
 * Lightness targets approximate Tailwind v3 palette curves; saturation is preserved
 * with a mild dampening at the extremes for a more pleasant ramp.
 */
export const TAILWIND_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

export function tailwindShades(hex: string): { step: number; hex: string }[] | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  const lightnessMap: Record<number, number> = {
    50: 97, 100: 94, 200: 86, 300: 76, 400: 64, 500: 52,
    600: 44, 700: 36, 800: 28, 900: 20, 950: 12,
  };
  return TAILWIND_STEPS.map((step) => {
    const l = lightnessMap[step];
    // dampen saturation near the extremes
    const satFactor = step <= 100 || step >= 900 ? 0.7 : 1;
    return {
      step,
      hex: hslToHex({ h: hsl.h, s: Math.min(100, hsl.s * satFactor), l }),
    };
  });
}

export type HarmonyKind = "complementary" | "analogous" | "triadic" | "tetradic" | "monochromatic";

export function harmony(hex: string, kind: HarmonyKind): string[] | null {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  const make = (offsets: number[]) =>
    offsets.map((o) => hslToHex({ h: hsl.h + o, s: hsl.s, l: hsl.l }));
  switch (kind) {
    case "complementary": return [hex, hslToHex({ ...hsl, h: hsl.h + 180 })];
    case "analogous": return make([-30, 0, 30]);
    case "triadic": return make([0, 120, 240]);
    case "tetradic": return make([0, 90, 180, 270]);
    case "monochromatic":
      return [20, 35, 50, 65, 80].map((l) => hslToHex({ ...hsl, l }));
  }
}

/**
 * An 8-bit sRGB channel (0-255) as linear light, per the WCAG relative-luminance definition:
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function linearChannel(value: number): number {
  const s = value / 255;
  // 0.04045 is the breakpoint in the current WCAG 2.1 / 2.2 text (the sRGB standard's value).
  // Before May 2021 WCAG said 0.03928; the spec notes that has no practical effect, and for
  // 8-bit input it has none at all (no channel value lies between the two thresholds).
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG 2.1 relative luminance (0-1) of an sRGB colour. */
export function relativeLuminance({ r, g, b }: RGB): number {
  return 0.2126 * linearChannel(r) + 0.7152 * linearChannel(g) + 0.0722 * linearChannel(b);
}

/** WCAG 2.1 relative luminance (0-1). Returns null for invalid hex input. */
export function perceivedLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  return rgb ? relativeLuminance(rgb) : null;
}

/**
 * WCAG 2.1 contrast ratio (1-21) between two hex colours, or null if either is
 * invalid. Unrounded, as the guidelines require (4.499 does not pass 4.5).
 */
export function contrastRatio(a: string, b: string): number | null {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return null;
  const la = relativeLuminance(ra);
  const lb = relativeLuminance(rb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Returns '#000000' or '#ffffff', whichever contrasts better against `bgHex`.
 * 0.179 is the luminance where the two contrast equally, sqrt(1.05 * 0.05) - 0.05
 * = 0.17913, rounded down: colours in that sliver get black although white is
 * better by under 0.006:1.
 */
export function autoForeground(bgHex: string): string {
  const L = perceivedLuminance(bgHex);
  return (L ?? 1) > 0.179 ? "#000000" : "#ffffff";
}
